import React, { useState, useEffect, useRef } from 'react';
import MedboardPdf from '../MedboardPdf/MedboardPdf.jsx';
import { EventEmitter } from "events";
import LoadingSpinnerImage from './LoadingSpinnerImage.js';

const ComplianceComparison = ({ AppContext, screenType, setScreenType, selectedPDFVersion, selectedPDFVersionComparison, closePopup }) => {

    const [comparisonChangesList, setComparisonChangesList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCompareAllSelected, setIsCompareAllSelected] = useState(false);

    const [activeSelectedChange, setActiveSelectedChange] = useState(null);

    const [selectedPDFTab1, setSelectedPDFTab1] = useState("main");
    const [selectedPDFTab2, setSelectedPDFTab2] = useState("main");

    const firstEmitterRef = useRef(new EventEmitter());
    const firstEmitter = firstEmitterRef.current;

    const secondEmitterRef = useRef(new EventEmitter());
    const secondEmitter = secondEmitterRef.current;

    const hasValue = (field) => {
        if (field != null && field != "")
            return true;

        return false;
    }

    useEffect(() => {
        if (screenType == "comparison" && selectedPDFVersionComparison) {

            // Load changes for documents
            let entityType = AppContext.entityType;
            let originalUniqueId = selectedPDFVersion.UniqueId;
            let comparisonUniqueId = selectedPDFVersionComparison.UniqueId;

            let list = [];

            let url = `/umbraco/api/DocumentAnalysis/GetRedline/?entityType=${entityType}&originalUniqueId=${originalUniqueId}&comparisonUniqueId=${comparisonUniqueId}`;

            let requestOptions = {
                method: 'GET',
            };

            if (true) {
                setIsLoading(true);

                fetch(url, requestOptions)
                    .then((response) => {
                        if (!response.ok) {
                            if (response.status === 401)
                                document.dispatchEvent(new Event("api:unauthorized"));

                            throw new Error("Network response was not ok");
                        }
                        return response.json();
                    })
                    .then((data) => {
                        // Check if the comparison backend result switched between original/new
                        if (data.OriginalUniqueId == originalUniqueId)
                            data.Changes.map(change => list.push(change));
                        else {
                            // Mark change as switched to switch in which document to apply
                            data.Changes.map(change => {
                                let switchedChange = { ...change, switched: true };
                                list.push(switchedChange);
                            });
                        }

                        // Give it a generic Id to be able to know if all is selected and a specific one was clicked to scroll to that one
                        list = list.map((item, index) => ({ ...item, Id: index + 1 }));

                        // For one-sided actions (delete, insert) try to add a coordinate for the null side so it can also scroll that document to the same page
                        list = fillMissingCoordinates(list);

                        setComparisonChangesList(list);
                    })
                    .catch((error) => {
                        console.log(error);
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }
        }
    }, [screenType, selectedPDFVersionComparison]);

    function fillMissingCoordinates(changes) {
        // Build page-size lookups
        const originalPageSizes = new Map();
        const newPageSizes = new Map();

        function collectSizes(coords, lookup) {
            if (!coords) return;

            coords.forEach(c => {
                if (
                    c?.Page != null &&
                    c?.PageWidth != null &&
                    c?.PageHeight != null
                ) {
                    lookup.set(c.Page, {
                        PageWidth: c.PageWidth,
                        PageHeight: c.PageHeight
                    });
                }
            });
        }

        // Pass 1: collect dimensions
        changes.forEach(change => {
            collectSizes(change.OriginalCoordinates, originalPageSizes);
            collectSizes(change.NewCoordinates, newPageSizes);
        });

        // Pass 2: fill missing side
        changes.forEach(change => {

            // INSERT: OriginalCoordinates missing
            if (
                !change.OriginalCoordinates &&
                change.NewCoordinates?.length
            ) {
                const page = change.NewCoordinates[0].Page;
                const size = originalPageSizes.get(page);

                change.OriginalCoordinates = size
                    ? [{
                        Page: page,
                        x0: 0,
                        x1: 0,
                        y0: 0,
                        y1: 0,
                        PageWidth: size.PageWidth,
                        PageHeight: size.PageHeight,
                        Id: null
                    }]
                    : // if not working replace by null
                    [{
                        Page: page,
                        x0: 0,
                        x1: 0,
                        y0: 0,
                        y1: 0,
                        PageWidth: 1,
                        PageHeight: 1,
                        Id: null
                    }];
            }

            // DELETE: NewCoordinates missing
            if (
                !change.NewCoordinates &&
                change.OriginalCoordinates?.length
            ) {
                const page = change.OriginalCoordinates[0].Page;
                const size = newPageSizes.get(page);

                change.NewCoordinates = size
                    ? [{
                        Page: page,
                        x0: 0,
                        x1: 0,
                        y0: 0,
                        y1: 0,
                        PageWidth: size.PageWidth,
                        PageHeight: size.PageHeight,
                        Id: null
                    }]
                    : // if not working replace by null
                    [{
                        Page: page,
                        x0: 0,
                        x1: 0,
                        y0: 0,
                        y1: 0,
                        PageWidth: 1,
                        PageHeight: 1,
                        Id: null
                    }];
            }
        });

        return changes;
    }

    // This function now only sets the active selection and tab states.
    // The useEffect below handles the actual emission.
    const highlightComparisonChange = (selectedChange = null) => {
        if (selectedChange) {
            setActiveSelectedChange(selectedChange);
            setSelectedPDFTab1("main");
            setSelectedPDFTab2("main");
        } else {
            setActiveSelectedChange(null);
        }
    };

    // Single source of truth for syncing highlights to the PDF viewers
    useEffect(() => {
        if (!comparisonChangesList || comparisonChangesList.length === 0) return;

        let changes = [];

        if (isCompareAllSelected) {
            changes = comparisonChangesList.map(change => {
                // Apply 'Special' styling to the currently active selection, if any
                if (activeSelectedChange && change.Id === activeSelectedChange.Id) {
                    return { ...change, Special: true };
                }
                return { ...change, Special: false };
            });
        } else {
            if (activeSelectedChange) {
                changes = [{ ...activeSelectedChange, Special: true }];
            } else {
                changes = [];
            }
        }

        let doc1Change = {
            Captures: changes.map(change => {
                let specialTag = change.Special ? "special_" : "";

                return {
                    Type: change.switched ? "_comparison_inserted_" + specialTag : "_comparison_deleted_" + specialTag,
                    Tokens: "",
                    Value: change.switched ? "Inserted" : "Deleted",
                    Coordinates: change.switched ? change.NewCoordinates : change.OriginalCoordinates,
                };
            })
        };

        let doc2Change = {
            Captures: changes.map(change => {
                let specialTag = change.Special ? "special_" : "";

                return {
                    Type: change.switched ? "_comparison_deleted_" + specialTag : "_comparison_inserted_" + specialTag,
                    Tokens: "",
                    Value: change.switched ? "Deleted" : "Inserted",
                    Coordinates: change.switched ? change.OriginalCoordinates : change.NewCoordinates
                };
            })
        };

        // Delay ensures MedboardPdf is fully mounted and listening, especially if tabs were just switched.
        // This fixes the "have to click twice to scroll/highlight" bug.
        const timer = setTimeout(() => {
            firstEmitter.emit("sethighlights", { highlights: doc1Change, appraisalType: "" });
            secondEmitter.emit("sethighlights", { highlights: doc2Change, appraisalType: "" });
        }, 150);

        return () => clearTimeout(timer);

    }, [activeSelectedChange, isCompareAllSelected, comparisonChangesList, selectedPDFTab1, selectedPDFTab2]);

    return <>
        <div>
            <div>
                <div style={{ display: "flex", flexDirection: "column", marginBottom: "15px" }}>
                    {/* Row 1: Main Title & Close Button */}
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "14px",
                        paddingBottom: "8px",
                        borderBottom: "1px solid #f0f0f0"
                    }}>
                        <h2 className="new-page-header-title" style={{ fontSize: "18px" }}>
                            <i className="fas fa-exchange-alt g-mr-8 g-ml-5" style={{ color: "rgb(43, 108, 176)" }}></i> Redline Comparison</h2>

                        <button className="close-button" onClick={closePopup} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                            <i className="fas fa-times" style={{ fontSize: "16px", color: "#000" }}></i>
                        </button>
                    </div>

                    {/* Row 2: Aligned Document Titles */}
                    <div style={{ display: "flex", alignItems: "center" }}>
                        {/* Left Document Title */}
                        <div style={{ flex: 1, marginRight: "20px" }}>
                            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#4a4a4a" }}>
                                {selectedPDFVersion.Title}
                            </h3>
                        </div>

                        {/* Right Document Title */}
                        <div style={{ flex: 1, marginRight: "20px" }}>
                            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#4a4a4a" }}>
                                {selectedPDFVersionComparison.Title}
                            </h3>
                        </div>

                        {/* Right Spacer: Matches the width of "Changes" sidebar*/}
                        <div style={{ width: "300px" }}>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ display: "flex", flexDirection: "row", gap: "15px" }}>

                {/* Left PDF Viewer (Document 1) */}
                <div style={{ flex: 1 }}>
                    {hasValue(selectedPDFVersion?.TranslationMediaPath) ? (
                        <div style={{ display: "flex", width: "100%", flexDirection: "row", marginBottom: "15px" }} >
                            <div onClick={() => setSelectedPDFTab1("main")} style={{ cursor: "pointer", paddingRight: "8px", paddingLeft: "8px" }} className={selectedPDFTab1 === "main" ? "pdf-viewer-tab-selected" : "pdf-viewer-tab-unselected"}><i className="fas fa-file-alt"></i> Original</div>
                            <div onClick={() => setSelectedPDFTab1("translation")} style={{ cursor: "pointer", paddingRight: "8px", paddingLeft: "8px" }} className={selectedPDFTab1 === "translation" ? "pdf-viewer-tab-selected" : "pdf-viewer-tab-unselected"}><i className="fas fa-language"></i> Translation</div>
                            <div style={{ flex: "1 1 0%", borderBottom: "2px solid rgb(240, 240, 240)" }}></div>
                        </div>
                    ) : null}

                    {selectedPDFTab1 === "main" ? (
                        <div id="pdfviewerdiv1" style={{ height: "100%", marginBottom: "30px" }}>
                            <MedboardPdf
                                key={`main-1-${selectedPDFVersion?.Id || 'doc1'}`}
                                updater={firstEmitter}
                                savedComments={[]}
                                url={
                                    hasValue(selectedPDFVersion?.MediaPath)
                                        ? selectedPDFVersion.MediaPath
                                        : ""
                                }
                                showHighlightsButton={false}
                                showAnnotationsButton={false}
                                containerHeight={"80vb"}
                            />
                        </div>
                    ) : null}

                    {selectedPDFTab1 === "translation" ? (
                        hasValue(selectedPDFVersion?.TranslationMediaPath) ? (
                            <div id="pdfviewerdiv1-translation" style={{ height: "100%", marginBottom: "30px" }}>
                                <MedboardPdf
                                    key={`trans-1-${selectedPDFVersion?.Id || 'doc1'}`}
                                    updater={firstEmitter}
                                    savedComments={[]}
                                    url={selectedPDFVersion.TranslationMediaPath}
                                    showHighlightsButton={false}
                                    showAnnotationsButton={false}
                                    containerHeight={"80vb"}
                                />
                            </div>
                        ) : (
                            <div style={{ marginTop: "15px", color: "#888" }}>No translation file found.</div>
                        )
                    ) : null}
                </div>

                {/* Right PDF Viewer (Document 2) */}
                <div style={{ flex: 1 }}>
                    {hasValue(selectedPDFVersionComparison?.TranslationMediaPath) ? (
                        <div style={{ display: "flex", width: "100%", flexDirection: "row", marginBottom: "15px" }} >
                            <div onClick={() => setSelectedPDFTab2("main")} style={{ cursor: "pointer", paddingRight: "8px", paddingLeft: "8px" }} className={selectedPDFTab2 === "main" ? "pdf-viewer-tab-selected" : "pdf-viewer-tab-unselected"}><i className="fas fa-file-alt"></i> Original</div>
                            <div onClick={() => setSelectedPDFTab2("translation")} style={{ cursor: "pointer", paddingRight: "8px", paddingLeft: "8px" }} className={selectedPDFTab2 === "translation" ? "pdf-viewer-tab-selected" : "pdf-viewer-tab-unselected"}><i className="fas fa-language"></i> Translation</div>
                            <div style={{ flex: "1 1 0%", borderBottom: "2px solid rgb(240, 240, 240)" }}></div>
                        </div>
                    ) : null}

                    {selectedPDFTab2 === "main" ? (
                        <div id="pdfviewerdiv2" style={{ height: "100%", marginBottom: "30px" }}>
                            <MedboardPdf
                                key={`main-2-${selectedPDFVersionComparison?.Id || 'doc2'}`}
                                updater={secondEmitter}
                                savedComments={[]}
                                url={
                                    hasValue(selectedPDFVersionComparison?.MediaPath)
                                        ? selectedPDFVersionComparison.MediaPath
                                        : ""
                                }
                                showHighlightsButton={false}
                                showAnnotationsButton={false}
                                containerHeight={"80vb"}
                            />
                        </div>
                    ) : null}

                    {selectedPDFTab2 === "translation" ? (
                        hasValue(selectedPDFVersionComparison?.TranslationMediaPath) ? (
                            <div id="pdfviewerdiv2-translation" style={{ height: "100%", marginBottom: "30px" }}>
                                <MedboardPdf
                                    key={`trans-2-${selectedPDFVersionComparison?.Id || 'doc2'}`}
                                    updater={secondEmitter}
                                    savedComments={[]}
                                    url={selectedPDFVersionComparison.TranslationMediaPath}
                                    showHighlightsButton={false}
                                    showAnnotationsButton={false}
                                    containerHeight={"80vb"}
                                />
                            </div>
                        ) : (
                            <div style={{ marginTop: "15px", color: "#888" }}>No translation file found.</div>
                        )
                    ) : null}
                </div>

                {/* Changes Sidebar */}
                <div style={{ width: "350px" }}>
                    <div style={{ backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "15px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", overflow: "hidden", }} >
                        {/* Header */}
                        <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between" }} >
                            <h4 style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#1f2937", letterSpacing: "0.3px" }} >
                                Changes {isLoading ? ("") : "(" + comparisonChangesList?.length + ")"}
                            </h4>
                            {!isLoading && (
                                <label
                                    style={{ display: "flex", alignItems: "center", fontSize: "14px", color: "#333", cursor: "pointer", margin: 0 }}
                                >
                                    <input
                                        type="checkbox"
                                        id="show-all-redlines"
                                        checked={isCompareAllSelected}
                                        onChange={(e) => {
                                            setIsCompareAllSelected(e.target.checked);
                                        }}
                                        style={{ marginRight: "6px", cursor: "pointer" }}
                                    />
                                    Show all
                                </label>
                            )}
                        </div>

                        {/* Content */}
                        <div style={{ padding: "16px", minHeight: "120px", display: "flex", alignItems: "center", justifyContent: "center", }} >
                            {isLoading ? (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", color: "#6b7280", fontSize: "12px", }} >
                                    <LoadingSpinnerImage />
                                    <span>Loading changes...</span>
                                </div>
                            ) : comparisonChangesList?.length === 0 ? (
                                <div
                                    style={{
                                        color: "#9ca3af",
                                        fontSize: "13px",
                                        textAlign: "center",
                                    }}
                                >
                                    No changes to display
                                </div>
                            ) : (
                                <div style={{ width: "100%" }}>
                                    <ChangesList comparisonChangesList={comparisonChangesList} highlightComparisonChange={highlightComparisonChange} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>
}

const ChangesList = ({ comparisonChangesList, highlightComparisonChange }) => {
    const [search, setSearch] = useState("");

    const filteredChanges = comparisonChangesList.filter((change) => {
        const Tag = change?.Tag ?? "";
        const OriginalText = change?.OriginalText ?? "";
        const NewText = change?.NewText ?? "";
        const TranslatedOriginalText = change?.TranslatedOriginalText ?? "";
        const TranslatedNewText = change?.TranslatedNewText ?? "";

        const searchableText =
            `${Tag} ${OriginalText} ${NewText} ${TranslatedOriginalText} ${TranslatedNewText}`.toLowerCase();

        return searchableText.includes(search.toLowerCase());
    });

    return (
        <div style={{ maxHeight: "calc(100vh - 200px)", display: "flex", flexDirection: "column" }}>
            {/* Search + Clear */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <input
                    type="text"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                        flex: 1,
                        padding: "8px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                    }}
                />

                {search && (
                    <button
                        onClick={() => setSearch("")}
                        style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #ccc",
                            background: "#f5f5f5",
                            cursor: "pointer",
                        }}
                    >
                        Clear
                    </button>
                )}
            </div>

            <div style={{ flex: 1, overflow: "auto" }}>
                {/* Filtered Results */}
                {filteredChanges.map((change, index) => (
                    <ChangeBlock
                        key={index}
                        change={change}
                        index={index}
                        onClick={(c) => {
                            highlightComparisonChange(c);
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

const ChangeBlock = ({ change, index, onClick }) => {
    const tagMap = {
        INSERT: "ADDED",
        DELETE: "DELETED",
        REPLACE: "REPLACED"
    };

    const Tag = tagMap[change?.Tag] ?? "";
    const TagColor = "";
    const TagColorFont = "#777";
    const OriginalText = change?.OriginalText ?? "";
    const NewText = change?.NewText ?? "";
    const TranslatedOriginalText = change?.TranslatedOriginalText ?? "";
    const TranslatedNewText = change?.TranslatedNewText ?? "";
    const PageNumber = change?.OriginalCoordinates?.[0]?.Page
        ?? change?.NewCoordinates?.[0]?.Page;
    const deletedCount = OriginalText.length;
    const addedCount = NewText.length;

    const renderContent = () => {
        switch (Tag) {
            case "ADDED":
                return (
                    <>
                        <div>
                            <span className="text-added" style={{ padding: "2px 4px" }}>
                                {NewText}
                            </span>
                            {TranslatedNewText && (
                                <div style={{ fontSize: "12px", color: "#6b7280", fontStyle: "italic", marginTop: "6px", paddingLeft: "4px" }}>
                                    {TranslatedNewText}
                                </div>
                            )}
                        </div>

                        <div style={{ fontSize: "12px", color: "#555", marginTop: "10px", textAlign: "right" }}>
                            <span className="charts-added">+{addedCount}</span> chars
                        </div>
                    </>
                );

            case "DELETED":
                return (
                    <>
                        <div>
                            <span className="text-deleted" style={{ padding: "2px 4px" }}>
                                {OriginalText}
                            </span>
                            {TranslatedOriginalText && (
                                <div style={{ fontSize: "12px", color: "#6b7280", fontStyle: "italic", marginTop: "6px", paddingLeft: "4px" }}>
                                    {TranslatedOriginalText}
                                </div>
                            )}
                        </div>

                        <div style={{ fontSize: "12px", color: "#555", marginTop: "10px", textAlign: "right" }}>
                            <span className="charts-deleted">-{deletedCount}</span> chars
                        </div>
                    </>
                );

            case "REPLACED":
                return (
                    <>
                        <div>
                            <div style={{
                                padding: "4px",
                                fontWeight: "600",
                                color: "#111827",
                                marginRight: "4px",
                                marginTop: "10px",
                                fontSize: "11px"
                            }}>
                                Before
                            </div>
                            <div>
                                <span className="text-deleted">{OriginalText}</span>
                                {TranslatedOriginalText && (
                                    <div style={{ fontSize: "12px", color: "#6b7280", fontStyle: "italic", marginTop: "6px", paddingLeft: "4px" }}>
                                        {TranslatedOriginalText}
                                    </div>
                                )}
                            </div>
                            <div style={{
                                padding: "4px",
                                fontWeight: "600",
                                color: "#111827",
                                marginRight: "4px",
                                marginTop: "10px",
                                fontSize: "11px"
                            }}>
                                After
                            </div>
                            <div>
                                <span className="text-added">{NewText}</span>
                                {TranslatedNewText && (
                                    <div style={{ fontSize: "12px", color: "#6b7280", fontStyle: "italic", marginTop: "6px", paddingLeft: "4px" }}>
                                        {TranslatedNewText}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{ fontSize: "12px", color: "#555", marginTop: "10px", textAlign: "right" }}>
                            <span className="charts-deleted">-{deletedCount}</span> / <span className="charts-added">+{addedCount}</span>  chars
                        </div>

                    </>
                );

            default:
                return null;
        }
    };

    return (
        <div
            onClick={() => onClick(change)}
            style={{
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "10px",
                marginBottom: "10px",
                cursor: "pointer",
            }}
        >
            {/* Header Section */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px"
            }}>
                {/* Left Side: Number and Badge Tag */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>

                    <span style={{
                        color: "#888888",
                        fontWeight: "normal",
                        fontSize: "11px",
                        marginRight: "4px"
                    }}>
                        #{index + 1}
                    </span>

                    <span style={{
                        fontWeight: "600",
                        fontSize: "12px",
                        backgroundColor: TagColor,
                        color: TagColorFont,
                        padding: "3px 8px",
                        borderRadius: "6px",
                        display: "inline-block",
                        lineHeight: "1"
                    }}>
                        {Tag}
                    </span>
                </div>

                {/* Right Side: Page Number */}
                <span style={{
                    fontStyle: "italic",
                    color: "#888888",
                    fontWeight: "normal",
                    fontSize: "11px",
                }}>
                    Page {PageNumber}
                </span>
            </div>

            {/* Content Section */}
            {renderContent()}
        </div>
    );
};

export default ComplianceComparison;